'use client';

import Image from 'next/image';
import Link from 'next/link';
import PriceDetails from '@/components/global/PriceDetails';
import { usePricing } from '@/contexts/PricingContext';
import { formatPrice } from '@/utils/utilities.mjs';
import elevarSelectItem from '@/lib/elevar/select-item';
import YotpoStars from '@/components/global/yotpo/YotpoStars';
import { buildContentstackImageUrl } from '@/lib/utils';
import CtaArrowIcon from '@/components/icons/CtaArrowIcon';
import CheckCircleIcon from '@/components/icons/CheckCircleIcon';
import { jsonToHtml } from '@contentstack/json-rte-serializer';

export default function CollectionProductCard({ item, position, size, priority = false }) {
  const pricing = usePricing();
  const { shopifyProduct } = item;
  const product = item.product?.[0];
  const { collection_card, product_name, button_label } = product ?? {};
  const { image } = collection_card || {};

  const descriptionHtml = collection_card?.description
    ? jsonToHtml(collection_card.description)
    : '';

  const uspList = (
    Array.isArray(collection_card?.usp_list) ? collection_card.usp_list : []
  )
  .map(function (uspText) {
    return uspText?.single_line_text;
  })
  .filter(Boolean);

  const variantForSize = (value) =>
    shopifyProduct?.variants?.find((v) =>
      v.selectedOptions?.some((o) => o.name === 'Size' && o.value === value),
    ) ?? null;

  // BL-518: price against the collection's own size when the CMS sets one, so
  // a Twin collection quotes Twin. Queen stays the target for collections with
  // no size — deliberately not the visitor's PDP selection, which persists
  // globally in localStorage and would be wrong here (BL-292).
  const targetSize = size || 'Queen';
  const exactVariant = variantForSize(targetSize);

  // Some products do not come in the target size at all — the kids mattress has
  // no Queen, pillows are Standard/King only. Those show their cheapest variant
  // as "From $X" rather than a size the shopper did not ask about: the price is
  // then explicitly a floor, not a like-for-like comparison with the cards
  // beside it. Cheapest is also stable, where the previous fallback took
  // whichever variant Shopify happened to return first.
  const cheapestVariant = (shopifyProduct?.variants ?? []).reduce(
    (cheapest, v) =>
      !cheapest || pricing.variant(v).sale < pricing.variant(cheapest).sale
        ? v
        : cheapest,
    null,
  );

  const variant = exactVariant ?? cheapestVariant ?? null;
  const url = product?.url || '#';

  const prices = variant ? pricing.variant(variant) : null;

  const handleElevarSelectItem = () => {
    if (shopifyProduct) {
      elevarSelectItem(
        shopifyProduct,
        window.location.pathname,
        position,
        pricing,
        variant,
      );
    }
  };

  return (
    <div className="flex flex-col overflow-hidden bg-white border border-sand-light shadow-[0_4px_16px_0_rgba(0,0,0,0.16)] h-full">
      {image?.url && (
        <Link href={url} className="block" onClick={handleElevarSelectItem}>
          <div className="relative w-full h-[250px]">
            <Image
              src={buildContentstackImageUrl(image.url, 768)}
              alt={image.description || image.title || product_name}
              fill
              className="object-cover w-full h-full"
              sizes="(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 401px"
              loading={priority ? 'eager' : undefined}
              fetchPriority={priority ? 'high' : undefined}
            />
          </div>
        </Link>
      )}

      <div className="p-5 md:p-6 flex flex-col gap-4 border-b border-sand-light flex-1">
        {/* BL-472: h2, not h3. collection_product_grid never renders a heading
            of its own, so these cards sit directly under the page h1 and an h3
            skipped a level. Unlike ProductCards, there is no optional block
            heading above them that would make h3 correct. */}
        <div>
          <h2 className="text-2xl font-cormorant font-bold capitalize">
            {product?.url ? (
              <Link href={url} className="no-underline" data-cy="collection-product-link" onClick={handleElevarSelectItem}>
                {product_name}
              </Link>
            ) : (
              product_name
            )}
          </h2>

          <YotpoStars productId={product?.shopify_product_id} url={product?.url} />
        </div>

        {uspList.length > 0 ? (
          <ul role="list" className="flex flex-col gap-1">
            {uspList.map((usp, i) => (
              <li
                key={i}
                className="flex gap-2 items-start text-sm text-primary"
              >
                <CheckCircleIcon />
                <span>{usp}</span>
              </li>
            ))}
          </ul>
        ) : (
          descriptionHtml && (
            <div className="text-base text-primary" dangerouslySetInnerHTML={{ __html: descriptionHtml }}></div>
          )
        )}
      </div>

      <div className="flex justify-between items-center px-5 py-3 md:py-4 md:px-6 mt-auto">
        <div className="flex flex-col gap-1">
          {prices !== null && (
            <>
              <span className="text-sm text-primary">Starting at</span>
              <span className="text-primary font-semibold text-base md:text-xl">
                {formatPrice(prices.sale)}
              </span>
              {prices.hasPriceDifference && (
                <span className="text-primary/75 line-through text-xs md:text-sm">
                  ({formatPrice(prices.full)})
                </span>
              )}
            </>
          )}
        </div>

        <div className="flex flex-col items-end gap-1">
          {product?.url && (
            <Link
              href={url}
              className="text-lg group flex items-center px-5 py-3 no-underline text-center bg-sea text-white rounded-full font-normal capitalize whitespace-nowrap"
              onClick={handleElevarSelectItem}
            >
              {button_label || 'Shop Now'}
              <CtaArrowIcon />
            </Link>
          )}
          <PriceDetails />
        </div>
      </div>
    </div>
  );
}
